import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Formik } from 'formik'
import * as Yup from 'yup'

import {
  BenefitDetailsResponse,
  EligibleExpenseCategory,
  PerksCategoriesWithExpenseResponse,
  addNewPerk,
  customExpenseType,
  deleteCustomExpenseType,
  editCustomExpenseType,
  getEligiblePerkCategoriesToAdd,
} from 'core'
import { PerksCategoriesWithExpenseRequest } from 'core/application/adminDashboard/benefitDetails/design/perks/productClass/addNewPerk'
import { frequencies } from 'core/domain/benefit/allowanceFrequency'

import {
  Button,
  CheckboxGroup,
  Col,
  DefaultCheckbox,
  DefaultDatePickerV2,
  DefaultInputV2,
  DefaultSelectV2,
  DeleteConfirmModal,
  Divider,
  Icon,
  Modal,
  Row,
  SimpleCheckbox,
  SimpleInput,
  Skeleton,
} from 'common/components'
import { formatYear, nowAsString, nowV2, parseZone } from 'common/dateFormatters'

import { useCommandRequestV2, useQueryRequestV2 } from '../../../../../common/hooks'
import SelectAndAddPerk from './SelectAndAddPerk'

import './style.less'

type NewPerkProps = {
  showAddNewPerkModal: boolean
  productClassId: string
  planPeriod: BenefitDetailsResponse
  offerSameAllowance: boolean
  eligibleExpenseCategories: EligibleExpenseCategory[]
  onCancel?: () => void
  onSubmit?: () => void
}
export const AddNewPerkModal = ({
  showAddNewPerkModal,
  productClassId,
  planPeriod,
  offerSameAllowance,
  eligibleExpenseCategories,
  onCancel,
  onSubmit,
}: NewPerkProps) => {
  const {
    data: categoriesEligible,
    isLoading: isCategoryLoading,
    reload: reloadCategories,
  } = useQueryRequestV2<PerksCategoriesWithExpenseResponse[]>({
    queryFn: () => getEligiblePerkCategoriesToAdd(productClassId, planPeriod?.id),
  })
  const { t: translate } = useTranslation()

  const [showEditCustomExpenseType, setEditShowCustomExpenseType] = useState<string | number>(0)
  const [deleteCustomExpense, setDeleteCustomExpense] = useState(null)
  const [showAddCustomExpenseType, setShowAddCustomExpenseType] = useState<string | number>(0)
  const [customExpenseTypeValue, setCustomExpenseTypeValue] = useState('')
  const [loadingExpenseType, setLoadingExpenseType] = useState(false)

  const { sendCommand: sendDeleteCustomExpenseType, isLoading: isCustomExpenseTypeDeletedLoading } =
    useCommandRequestV2((expenseTypeId: number) => deleteCustomExpenseType(productClassId, expenseTypeId))

  const { sendCommand: sendSaveCustomExpenseType, isLoading: isCustomExpenseTypeSavedLoading } = useCommandRequestV2(
    ({ name, categoryId }) => customExpenseType(name, categoryId, productClassId),
  )

  const { sendCommand: sendEditCustomExpenseType, isLoading: isCustomExpenseTypeLoading } = useCommandRequestV2(
    ({ name, categoryId, expenseTypeId }) => editCustomExpenseType(name, categoryId, expenseTypeId),
  )

  const { sendCommand: sendAddNewPerk, isLoading: isCommandLoading } = useCommandRequestV2(
    (request: PerksCategoriesWithExpenseRequest) => addNewPerk(request),
  )
  const disableEndDate = (formik, e) => {
    if (e.target.checked) {
      formik.setFieldValue(`categoryEndDate`, null)
    }
    formik.setFieldValue(`foreverPerk`, e.target.checked)
  }

  const reloadAfterSubmit = (perkId: string, perkName, formik) => {
    reloadCategories().then(() => {
      formik.setFieldValue('perkId', perkId)
    })
  }

  const getSharedAllowancePerk = () => {
    const nowDate = nowV2()
    if (eligibleExpenseCategories.length > 0) {
      const futurePerk = eligibleExpenseCategories.find((x) => nowDate.isBefore(x.endDate))
      const pastPerk = eligibleExpenseCategories.find((x) => nowDate.isSameOrAfter(x.endDate))
      return futurePerk ? futurePerk : pastPerk
    }
  }

  const isActivePerk = offerSameAllowance && nowV2().isSameOrBefore(getSharedAllowancePerk().endDate)
  const handleDeleteCustomExpenseType = async (expenseTypeId, formikValue, formik) => {
    setLoadingExpenseType(true)
    const result = await sendDeleteCustomExpenseType(expenseTypeId)
    if (result.completed) {
      await reloadCategories().then(() => {
        const eligibleCategoryIds = formikValue.filter((x) => x !== expenseTypeId)
        formik.setFieldValue('eligibleCategoryIds', eligibleCategoryIds)
        setDeleteCustomExpense(null)
        setLoadingExpenseType(false)
      })
    }
  }

  const handleSaveCustomExpenseType = async (categoryId, name, formikValue, formik) => {
    setLoadingExpenseType(true)
    const result = await sendSaveCustomExpenseType({ name, categoryId })
    if (result.completed) {
      reloadCategories().then(() => {
        const eligibleCategoryIds = [...formikValue, result.data]
        formik.setFieldValue('eligibleCategoryIds', eligibleCategoryIds)
        setShowAddCustomExpenseType(0)
        setLoadingExpenseType(false)
      })
      setCustomExpenseTypeValue('')
    }
  }

  const handleEditCustomExpenseType = async (categoryId, name, expenseTypeId) => {
    setLoadingExpenseType(true)
    const result = await sendEditCustomExpenseType({ name, categoryId, expenseTypeId })
    if (result.completed) {
      reloadCategories().then(() => {
        setEditShowCustomExpenseType(0)
        setCustomExpenseTypeValue('')
        setLoadingExpenseType(false)
      })
    }
  }

  const submit = async (values: any, productClassId: string) => {
    if (values.eligibleCategoryIds.length > 0) {
      const request = {
        perkCategoryId: values.perkId,
        expenseTypes: values.eligibleCategoryIds,
        startDate: values.categoryStartDate,
        endDate: values.categoryEndDate,
        amount: values.categoryAllowance,
        productClassId: productClassId,
        allowanceFrequency: values.allowanceFrequency,
      }
      const result = await sendAddNewPerk(request)

      if (result.hasErrors) return
      onSubmit()
    }
  }
  return (
    <>
      <Formik
        enableReinitialize={true}
        validationSchema={formValidator(translate)}
        validateOnMount
        initialValues={{
          perkId: null,
          eligibleCategoryIds: [],
          categoryStartDate: nowAsString(),
          categoryEndDate:
            offerSameAllowance &&
            isActivePerk &&
            formatYear(getSharedAllowancePerk().endDate) !== formatYear(planPeriod?.endDate)
              ? getSharedAllowancePerk().endDate
              : null,
          categoryAllowance: isActivePerk ? getSharedAllowancePerk().allowance : null,
          allowanceFrequency: isActivePerk ? getSharedAllowancePerk().allowanceFrequency : frequencies[0].id,
          foreverPerk:
            offerSameAllowance &&
            isActivePerk &&
            formatYear(getSharedAllowancePerk().endDate) !== formatYear(planPeriod?.endDate)
              ? false
              : offerSameAllowance &&
                isActivePerk &&
                formatYear(getSharedAllowancePerk().endDate) === formatYear(planPeriod?.endDate),
        }}
        onSubmit={() => {}}
      >
        {(formik) => (
          <Modal
            okButtonProps={{
              disabled: !formik.isValid || isCommandLoading,
              loading: isCommandLoading,
            }}
            title="Add a new perk category"
            onCancel={() => {
              onCancel()
            }}
            okText={'Save'}
            visible={showAddNewPerkModal}
            onOk={() => submit(formik.values, productClassId)}
            width={1000}
          >
            <Skeleton loading={isCategoryLoading && !loadingExpenseType}>
              {categoriesEligible && (
                <form onSubmit={formik.handleSubmit} autoComplete="off" onBlur={formik.handleBlur}>
                  <div>
                    <SelectAndAddPerk
                      perkCategories={categoriesEligible}
                      name={'perkId'}
                      onChange={(value) => {
                        formik.setValues({
                          ...formik.values,
                          perkId: value,
                        })
                      }}
                      onSubmit={(perkId: string, perkName: string) => {
                        reloadAfterSubmit(perkId, perkName, formik)
                      }}
                      productClassId={productClassId}
                    />
                    {formik.values.perkId && (
                      <div className={'add-new-perk-modal-wrapper w-100'}>
                        <CheckboxGroup name="eligibleCategoryIds">
                          <div className={'single-category-wrapper'}>
                            {categoriesEligible.find((x) => x.id === formik.values.perkId)?.expenseTypes &&
                              categoriesEligible
                                .find((x) => x.id === formik.values.perkId)
                                .expenseTypes.map((expenseType) => (
                                  <Row className="mb-8">
                                    {showEditCustomExpenseType !== 0 && showEditCustomExpenseType === expenseType.id ? (
                                      <Col span={24}>
                                        <Row className="flex__align_center">
                                          <SimpleCheckbox value={expenseType.id} key={expenseType.id}></SimpleCheckbox>
                                          <SimpleInput
                                            onPressEnter={(e) => {
                                              e.stopPropagation()
                                              e.preventDefault()
                                            }}
                                            className={'input-style'}
                                            defaultValue={expenseType.name}
                                            autoFocus
                                            onChange={(e) => {
                                              setCustomExpenseTypeValue(e.target.value)
                                            }}
                                          />
                                          <Button
                                            type="primary"
                                            icon="far fa-check"
                                            className="ml-8"
                                            loading={isCustomExpenseTypeLoading}
                                            disabled={isCustomExpenseTypeLoading}
                                            onClick={() =>
                                              handleEditCustomExpenseType(
                                                formik.values.perkId,
                                                customExpenseTypeValue,
                                                expenseType.id,
                                              )
                                            }
                                          >
                                            Submit
                                          </Button>
                                          <Button
                                            type="default"
                                            className="ml-8"
                                            onClick={() => {
                                              setEditShowCustomExpenseType('')
                                            }}
                                          >
                                            Cancel
                                          </Button>
                                        </Row>
                                      </Col>
                                    ) : (
                                      <Col span={24}>
                                        <Row>
                                          <SimpleCheckbox value={expenseType.id} key={expenseType.id}>
                                            {expenseType.name}
                                          </SimpleCheckbox>
                                          {expenseType.isCustom &&
                                            formik.values?.eligibleCategoryIds?.find((i) => i === expenseType.id) && (
                                              <Row className="px-8">
                                                <Col>
                                                  <Icon
                                                    className="fas fa-pencil pointer"
                                                    onClick={() => setEditShowCustomExpenseType(expenseType.id)}
                                                  />
                                                </Col>
                                                <Col>
                                                  <Divider orientation="center" type="vertical" />
                                                </Col>
                                                <Col>
                                                  <Icon
                                                    className="color-red-6 fa fa-trash pointer"
                                                    onClick={() => setDeleteCustomExpense(expenseType)}
                                                  />
                                                </Col>
                                              </Row>
                                            )}
                                        </Row>
                                      </Col>
                                    )}
                                  </Row>
                                ))}
                            {showAddCustomExpenseType !== 0 && showAddCustomExpenseType === formik.values.perkId && (
                              <Row>
                                <SimpleCheckbox></SimpleCheckbox>
                                <div></div>
                                <SimpleInput
                                  onPressEnter={(e) => {
                                    e.stopPropagation()
                                    e.preventDefault()
                                  }}
                                  className={'input-style'}
                                  autoFocus
                                  onChange={(e) => {
                                    setCustomExpenseTypeValue(e.target.value)
                                  }}
                                  name={'custom-category-type'}
                                />
                                <Button
                                  type="primary"
                                  icon="far fa-check"
                                  className="ml-8"
                                  loading={isCustomExpenseTypeSavedLoading}
                                  disabled={isCustomExpenseTypeSavedLoading}
                                  onClick={() =>
                                    handleSaveCustomExpenseType(
                                      formik.values.perkId,
                                      customExpenseTypeValue,
                                      formik.values?.eligibleCategoryIds,
                                      formik,
                                    )
                                  }
                                >
                                  Submit
                                </Button>
                                <Button type="default" className="ml-8" onClick={() => setShowAddCustomExpenseType(0)}>
                                  Cancel
                                </Button>
                              </Row>
                            )}
                            <Row className="mt-8">
                              <Col span={24}>
                                <h6>Can't find what you are looking for?</h6>
                              </Col>
                              <Col span={24}>
                                <Button
                                  type="primary"
                                  ghost
                                  icon="far fa-plus"
                                  disabled={showAddCustomExpenseType === formik.values.perkId}
                                  onClick={() => {
                                    setShowAddCustomExpenseType(formik.values.perkId)
                                  }}
                                >
                                  Add a custom category
                                </Button>
                              </Col>
                            </Row>
                          </div>
                        </CheckboxGroup>
                      </div>
                    )}

                    <div className={'add-new-perk-modal-wrapper'}>
                      <div className={'single-category-wrapper'}>
                        <Row justify={'space-between'}>
                          <Col xs={24} md={11}>
                            <DefaultInputV2
                              prefix={<Icon type="far fa-dollar-sign" fontAwesome />}
                              //type={'number'}
                              label={'Amount'}
                              placeholder={'0.00'}
                              disabled={isActivePerk}
                              name={`categoryAllowance`}
                            />
                            <DefaultDatePickerV2
                              name={`categoryStartDate`}
                              id={`categoryStartDate`}
                              className={'perk-start-date-picker w-100'}
                              disabled={isActivePerk}
                              disabledDate={(d) => !d || d.add(-1, 'days').isBefore(nowV2(), 'day')}
                              label="Start Date"
                              onChange={(date) => {
                                if (date) {
                                  formik.setFieldValue(`categoryEndDate`, null)
                                }
                              }}
                            />
                            <DefaultCheckbox
                              label={'Continuous'}
                              name={`foreverPerk`}
                              disabled={isActivePerk}
                              className="no-select checkbox-text"
                              onChange={(e) => {
                                disableEndDate(formik, e)
                              }}
                            >
                              Continuous
                            </DefaultCheckbox>
                          </Col>
                          <Col xs={24} md={12}>
                            <DefaultSelectV2
                              name={`allowanceFrequency`}
                              label={'Choose frequency'}
                              disabled={isActivePerk}
                              items={frequencies}
                            />

                            <DefaultDatePickerV2
                              className={'perk-end-date-picker mb-16 w-100'}
                              label={'End Date'}
                              name={`categoryEndDate`}
                              id={`categoryEndDate`}
                              disabled={!!formik.values.foreverPerk || isActivePerk}
                              disabledDate={(d) =>
                                !d || d.isBefore(parseZone(formik.values.categoryStartDate).add(1, 'days'))
                              }
                            />
                          </Col>
                        </Row>
                      </div>
                    </div>
                  </div>
                  {/*here*/}

                  <DeleteConfirmModal
                    title={`Deleting '${deleteCustomExpense?.name}' ...`}
                    content={'Are you sure you want to delete this custom category? This action cannot be undone.'}
                    onDelete={async () =>
                      handleDeleteCustomExpenseType(deleteCustomExpense.id, formik.values?.eligibleCategoryIds, formik)
                    }
                    onCancel={() => {
                      setDeleteCustomExpense(null)
                    }}
                    deleteButtonLoading={isCustomExpenseTypeDeletedLoading}
                    visible={!!deleteCustomExpense}
                  />
                </form>
              )}
            </Skeleton>
          </Modal>
        )}
      </Formik>
    </>
  )
}
const formValidator = (translate: any) =>
  Yup.object({
    perkId: Yup.number().nullable().required('Perk category is required.'),
    eligibleCategoryIds: Yup.array().required('Select sub categoires'),
    categoryStartDate: Yup.date().required('Category start date is required.'),
    categoryAllowance: Yup.number()
      .min(1, translate('admin-dashboard.benefit-details.class-design.errors.positive-number'))
      .typeError(translate('admin-dashboard.benefit-details.class-design.errors.enter-amount'))
      .required(translate('general.required')),
    allowanceFrequency: Yup.number().required(translate('general.required')),
    categoryEndDate: Yup.mixed().when('foreverPerk', {
      is: true,
      then: Yup.date().nullable(),
      otherwise: Yup.date().nullable().required('Enter an end date'),
    }),
  })
